import {AttrsSchema} from 'panel';
import {Hooks} from 'snabbdom';
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  Placement,
  size,
  VirtualElement,
} from '@floating-ui/dom';

import {StackableComponent, Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';
import {IronContextRegistry} from 'common/report/context-registry';

import {Directions, PositioningStrategy, PositionParentType} from './types';
import {template} from './template';
import css from './index.inline.styl';

export interface DropMenuAttrs {
  'allowed-flip-directions'?: Directions[];
  'disable-auto-focus'?: boolean;
  'experimental-positioning'?: boolean;
  'position-strategy'?: PositioningStrategy;
  'prevent-close-selectors'?: string[];
  'prevent-close'?: boolean;
  'show-arrow'?: boolean;
  direction?: Directions;
  elRef?: string;
  offset?: number;
  open?: boolean;
  skidding?: number;
}

export type PositionParent = PositionParentType | HTMLElement | VirtualElement;

export interface DropMenuProps {
  attrs?: DropMenuAttrs;
  on?: {
    change?: (ev: VisibilityChangeEvent) => void;
  };
  props?: {
    positionParent?: PositionParent;
  };
  hook?: Hooks;
}

export const FLOATING_POSITION_OVERRIDES: {[key in Directions]: Placement} = {
  [Directions.Down]: `bottom-start`,
  [Directions.DownRight]: `bottom-end`,
  [Directions.Up]: `top-start`,
  [Directions.Right]: `right-start`,
  [Directions.CenterRight]: `right`,
  [Directions.Left]: `left-start`,
  [Directions.CenterUp]: `top`,
  [Directions.CenterDown]: `bottom`,
};

export class FloatingUIDropMenu extends StackableComponent<EmptyObj, DropMenuAttrs, IronContextRegistry> {
  private floaterCleanup: () => void = null;
  private lastPlacement: Placement = null;
  private allowFlip = true;
  private _positionParent: PositionParent = null;
  private _positionParentEl: HTMLElement | VirtualElement = null;

  static get attrsSchema(): AttrsSchema<DropMenuAttrs> {
    return {
      ...super.attrsSchema,
      open: {
        description: `Is the dropdown menu open?`,
        type: `boolean`,
      },
      offset: {type: `number`, default: 4},
      skidding: {type: `number`, default: 0, description: `Amount of shifting parallel to parent`},
      'show-arrow': {type: `boolean`, description: `Whether to display an arrow b/w the tooltip and parent`},
      direction: {
        description: `Menu opens on the top of an element if 'up', on the bottom if 'down'`,
        type: `string`,
        enum: Object.values(Directions),
        default: Directions.Down,
      },
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Fixed,
        description: `Defines how the menu is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'allowed-flip-directions': {
        type: `json`,
        default: null,
        description: `Defines preferred flipping directions for whenever the dropdown cannot be fitted in view`,
      },
      'experimental-positioning': `boolean`,
      'prevent-close': {
        type: `boolean`,
        description: `When true, prevent drop menu from closing by regular means such as clicking outside of the component.`,
      },
      'disable-auto-focus': {
        type: `boolean`,
        description: `When true, prevent default StackableComponent behavior of focusing the first focusable child when the menu is open.`,
      },
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
    };
  }

  set positionParent(positionParent: PositionParent) {
    positionParent ||= PositionParentType.OffsetParent;

    if (this._positionParent !== positionParent) {
      this._positionParent = positionParent;
      this._positionParentEl =
        typeof positionParent === `string` ? (this[positionParent] as HTMLElement) : positionParent;
      this.cleanupFloatingUI();
      this.initialized && this.state.visibility === Visibility.Open && this.setupFloatingUI();
    } else if (this.floaterCleanup) {
      this.updateFloatingPosition();
    }
  }

  connectedCallback() {
    this.classList.add(`floating-ui`);
    super.connectedCallback();

    if (this._positionParent === null) {
      this.positionParent = PositionParentType.OffsetParent;
    }

    if (this.attr(`open`)) {
      this.open();
    }
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  get trapTab() {
    return false;
  }

  /**
   * When a dropdown is absolute positioned relative to another container we
   * make the assumption that the container is fixed and is the top most stacking
   * context in the page, in that case we want the dropdown to display as a tooltip
   */
  get increaseStackingContext() {
    return this.attr(`position-strategy`) === PositioningStrategy.Fixed;
  }

  updateFloatingPosition = () => {
    // if there's no parent element to attach to, this either means the dropdown's parent element has been
    // disconnected and out of view, setting up floating-ui is not needed in those cases
    if (!this._positionParentEl) {
      this.close();
      return null;
    }

    // floating-ui does not flip placement variations (*-start or *-end) to their opposite corner if that
    // placement overflows from the alternate axis (if bottom, this is the X axis), even though the page
    // might scroll to display the overflowing area from the alternative axis, we avoid
    // that situation by defaulting to the *-end position of the desired placement
    const placement = FLOATING_POSITION_OVERRIDES[this.attr(`direction`)];
    if (this.getBoundingClientRect().right >= window.innerWidth) {
      if (placement.startsWith(`bottom`)) {
        this.lastPlacement = `bottom-end`;
      } else if (placement.startsWith(`top`)) {
        this.lastPlacement = `top-end`;
      }
    }

    const arrowEl = this.attr(`show-arrow`) ? this.el.querySelector<HTMLElement>(`.mp-drop-menu-arrow`) : null;

    computePosition(this._positionParentEl, this, {
      placement: this.lastPlacement || placement,
      strategy: this.attr(`position-strategy`),
      middleware: [
        // hides the floating element when the reference element goes out of view
        // for example, when scrolling down a page, we don't want the floating element
        // to appear visually attached to an element out of view
        hide(),

        // pushes the floating element away from its reference
        offset(this.attr(`offset`) + (this.attr(`show-arrow`) ? 4 : 0)),

        // adds an arrow pointer around the floating element
        arrowEl && arrow({padding: 4, element: arrowEl}),

        // resizes the floating element so that it fits into screen
        this.attr(`experimental-positioning`) &&
          size({
            apply({availableHeight, elements}) {
              // Do things with the data, e.g.
              Object.assign(elements.floating.style, {
                maxHeight: `${availableHeight}px`,
              });
            },
          }),

        // prevents the floating element to overflow the screen
        this.allowFlip &&
          flip({
            rootBoundary: `viewport`,
            fallbackStrategy: `bestFit`,
            fallbackPlacements: this.attr(`allowed-flip-directions`)?.map(
              (direction) => FLOATING_POSITION_OVERRIDES[direction],
            ),
          }),
      ],
    }).then(({x, y, placement: finalPlacement, strategy, middlewareData}) => {
      const {referenceHidden, escaped} = middlewareData.hide;
      Object.assign(this.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: strategy,
        visibility: referenceHidden || escaped ? `hidden` : `visible`,
      });

      this.setAttribute(`data-popper-placement`, finalPlacement);

      if (middlewareData.arrow) {
        const {x: arrowX, y: arrowY} = middlewareData.arrow;
        Object.assign(arrowEl.style, {
          left: `${arrowX}px`,
          top: `${arrowY}px`,
        });
      }

      if (this.lastPlacement !== finalPlacement) {
        this.lastPlacement = finalPlacement;
      }

      // whenever a floating element goes out of view trigger a close event so that when scroll
      // location changes later, the floating element does not suddenly appears in view
      if (referenceHidden || escaped) {
        this.close();
      }
    });
  };

  /**
   * Setups the positioning library for the dropdown, for more info see:
   * https://floating-ui.com/docs/getting-started
   */
  setupFloatingUI() {
    // if there's no parent element to attach to, this either means the dropdown's parent element has been
    // disconnected and out of view, setting up the floating element is not needed in those cases
    if (!this._positionParentEl) {
      this.close();
      return null;
    }

    this.floaterCleanup = autoUpdate(this._positionParentEl, this, this.updateFloatingPosition);

    return this.floaterCleanup;
  }

  protected onOpen() {
    super.onOpen();

    this._positionParentEl =
      typeof this._positionParent === `string` ? (this[this._positionParent] as HTMLElement) : this._positionParent;

    this.setupFloatingUI();
  }

  /** gets called on disconnectedCallback from the StackableComponent implementation */
  protected onClose() {
    super.onClose();
    this.cleanupFloatingUI();
    this.lastPlacement = null;
    this._positionParentEl = null;
    this.removeAttribute(`data-popper-placement`);
    this.removeAttribute(`style`);
  }

  cleanupFloatingUI() {
    if (this.floaterCleanup) {
      this.floaterCleanup();
      this.floaterCleanup = null;
    }
  }

  isCloseable() {
    return !this.attr(`prevent-close`);
  }

  get shouldAutoFocus(): boolean {
    return !this.attr(`disable-auto-focus`);
  }
}
